import { putResolve } from 'redux-saga/effects';

import type { MainHubSubmitStakeError } from './staking/types';
import type { SagaIterator } from '@redux-saga/types';
import type {
  BetEntryPayload,
  CheckAvailabilityRequestBody,
} from 'app/api/services/bet-slip/check-availability/types';

import type { CheckAvailabilitySuccessPayload } from '../actions';
import { checkAvailability } from '../actions';
import type { AvailabilityStakesDAO } from '../types/backend/availableStakes';
import type { EntriesBets } from '../types/entries';
import { getAvailabilityResult } from '../utils/availability-saga';

// ---

export function getAvailabilityPayload(bets: EntriesBets): CheckAvailabilityRequestBody {
  return {
    bets: bets.reduce((bets: BetEntryPayload[], { entity, price }) => {
      bets.push({
        id: entity.id,
        matchId: entity.matchId,
        price,
      });

      return bets;
    }, []),
  };
}

type CheckAvailabilitySaga = SagaIterator<
  | CheckAvailabilitySuccessPayload
  | MainHubSubmitStakeError
  | Error
>;

type ErrorResponse = {
  error: { betErrors: Error };
};

/**
 * Request available stake types for a given bet entries.
 *
 * This saga dispatches the whole request flow actions i.e: REQUEST then SUCCESS
 * or FAILURE through the redux store and returns the available stake types results
 * to give the ability to detect conflicts at the upper level flow (saga).
 */
function* checkAvailabilitySaga(entries: EntriesBets): CheckAvailabilitySaga {
  const payload: CheckAvailabilityRequestBody = getAvailabilityPayload(entries);

  try {
    const response: {
      payload: {
        data: AvailabilityStakesDAO | ErrorResponse;
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } = yield putResolve(checkAvailability(payload));

    if ('error' in response.payload.data) {
      throw new Error('betError', { cause: response.payload.data.error.betErrors });
    }

    return getAvailabilityResult(entries, response.payload.data);
  }
  catch (error) {
    if (isCauseInError(error)) {
      throw new Error('betError', { cause: error.cause });
    }

    console.error('checkAvailabilitySaga -> unknown error type', error);
    throw new Error();
  }
}

export default checkAvailabilitySaga;

export function isCauseInError(err: unknown): err is Error {
  return err instanceof Object && 'cause' in err;
}
